import { useRef, useState, useEffect, useContext } from "react"
import axios from "axios"
import { Toast } from "primereact/toast"
import { Button } from "primereact/button"
import { Dialog } from "primereact/dialog"
import { Calendar } from "primereact/calendar"
import { InputText } from "primereact/inputtext"
import { Dropdown } from "primereact/dropdown"
import { RadioButton } from "primereact/radiobutton"
import { useQuery } from "@apollo/client"
import { UserContext } from "../../../context/userContext"
import { employmentApiUrl } from "../../../config/microservicesUrls"
import styles from "../../../styles/Participant-Employment.module.css"
import JobSearchPlan from "./JobSearchPlanForm"
import JobRecord from "./JobRecordForm"
import EndJobForm from "./EndJobForm"
import {
  showError,
  GET_USER,
  Actions,
  groupBy,
  getDuration,
  renderTableDataCell,
  getStartAndEndTime,
  convertToLocalDateTime,
  formatDate,
} from "../Utils/Utils"
import {
  Frequencies,
  SearchGoalStatuses,
  EngagementChannels,
} from "../Types/Dropdowns"
import { LoaderMedium } from "../../../components/Loaders"
import NoData from "../Utils/NoData"
import { ReactComponent as TaskCompleteIcon } from "../resources/task_complete.svg"
import JobRecordView from "./JobRecordView"
import ViewHeader from "../Utils/ViewHeader"

const api_url = employmentApiUrl()

export default function EmploymentJobIndex(props) {
  const toast = useRef(null)
  const userCtx = useContext(UserContext)
  const [loading, setLoading] = useState(true)
  const [showJobSearchPlanForm, setShowJobSearchPlanForm] = useState(false)
  const [jobSearchPlan, setJobSearchPlan] = useState(null)
  const [searchPlanAction, setSearchPlanAction] = useState(Actions.Add)
  const [showJobRecordForm, setShowJobRecordForm] = useState(false)
  const [jobRecord, setJobRecord] = useState(null)
  const [jobRecordAction, setJobRecordAction] = useState(Actions.Add)
  const [jobRecords, setJobRecords] = useState([])
  const [searchGoalDialogVisible, setSearchGoalDialogVisible] = useState(false)
  const [searchGoalToDelete, setSearchGoalToDelete] = useState(null)
  const [searchGoalAction, setSearchGoalAction] = useState(Actions.Add)
  const [minTargetDate, setMinTargetDate] = useState(formatDate(new Date()))
  const defaultSearchGoalData = {
    id: 0,
    goal: "",
    status: SearchGoalStatuses[0],
    responsable: "",
    frequency: Frequencies[0],
    otherFrequency: undefined,
    targetDate: formatDate(new Date()),
  }
  const [searchGoal, setSearchGoal] = useState(defaultSearchGoalData)
  const [loadingSearchGoal, setLoadingSearchGol] = useState(false)
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false)
  const [showEndJobForm, setShowEndJobForm] = useState(false)

  const participantId = userCtx.current_participant_id
  const organizationId = userCtx.session.user.organization_id
  const referralId = props.viewMode?.view
    ? props.viewMode?.referral.id
    : userCtx.currentActiveReferral

  const clearSearchGoal = () => {
    setSearchGoal(defaultSearchGoalData)
    setSearchGoalAction(Actions.Add)
  }

  const saveSearchGoal = () => {
    setLoadingSearchGol(true)
    const status = SearchGoalStatuses.indexOf(searchGoal.status)
    const frequency = Frequencies.indexOf(searchGoal.frequency)
    const targetDate = formatDate(searchGoal.targetDate)
    const jobSearchPlanId = jobSearchPlan.id

    const data = {
      ...searchGoal,
      jobSearchPlanId,
      status,
      frequency,
      targetDate,
    }
    if (searchGoalAction === Actions.Add) {
      axios
        .post(`${api_url}/jobSearchPlan/searchGoal`, data, {
          headers: { Authorization: `Bearer ${userCtx.tokenEmployment}` },
        })
        .then(function (response) {
          setSearchGoalDialogVisible(false)
          clearSearchGoal()
          data.id = response.data.id
          data.targetDate = response.data.targetDate
          setJobSearchPlan({
            ...jobSearchPlan,
            searchGoals: [...jobSearchPlan.searchGoals, data],
          })
          setLoadingSearchGol(false)
        })
        .catch(function (response) {
          setLoadingSearchGol(false)
          const errors = response["response"]["data"]["errors"]
          showError(toast, "Not able to create job search goal", errors)
        })
    } else {
      axios
        .put(`${api_url}/jobSearchPlan/searchGoal`, data, {
          headers: { Authorization: `Bearer ${userCtx.tokenEmployment}` },
        })
        .then(function (response) {
          setLoadingSearchGol(false)
          setLoading(false)
          clearSearchGoal()
          const searchGoals = jobSearchPlan.searchGoals.map((s) => {
            if (s.id === searchGoal.id) {
              return {
                ...searchGoal,
                jobSearchPlanId,
                status,
                frequency,
                targetDate: response.data.targetDate,
              }
            }
            return s
          })
          setJobSearchPlan({
            ...jobSearchPlan,
            searchGoals,
          })
          setSearchGoalDialogVisible(false)
        })
        .catch(function (response) {
          setLoadingSearchGol(false)
          const errors = response["response"]["data"]["errors"]
          setLoading(false)
          showError(toast, "Not able to update job search goal", errors)
        })
    }
  }

  const deleteSearchGoal = () => {
    axios
      .delete(
        `${api_url}/jobSearchPlan/searchGoal/?searchGoalId=${searchGoalToDelete.id}`,
        { headers: { Authorization: `Bearer ${userCtx.tokenEmployment}` } }
      )
      .then(function (response) {
        const searchGoals = jobSearchPlan.searchGoals.filter(
          (s) => s !== searchGoalToDelete
        )
        setJobSearchPlan({ ...jobSearchPlan, searchGoals })
        setSearchGoalToDelete(null)
        toast.current.show({ detail: "Search goal deleted" })
      })
      .catch(function (response) {
        setSearchGoalToDelete(null)
        const errors = response["response"]["data"]["errors"]
        showError(toast, "Not able to delete search goal", errors)
      })
  }

  const renderDeleteSearchGoalConfirmation = () => {
    const footerContent = (
      <div className={styles.buttons}>
        <Button
          className={styles.cancel_button}
          label="No"
          onClick={() => setDeleteConfirmationVisible(false)}
          autoFocus
        />
        <Button
          className={styles.button}
          label="Yes"
          onClick={() => {
            deleteSearchGoal()
            setDeleteConfirmationVisible(false)
          }}
        />
      </div>
    )
    return searchGoalToDelete != null ? (
      <Dialog
        header="Delete confirmation"
        modal={false}
        visible={deleteConfirmationVisible}
        className="custom_dialog"
        style={{ width: "50vw" }}
        onHide={() => setDeleteConfirmationVisible(false)}
        footer={footerContent}
      >
        <p>{`Are you sure you want to delete the job search goal?`}</p>
      </Dialog>
    ) : null
  }

  const { refetch } = useQuery(GET_USER, {
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
    },
  })

  const getStaffName = async (staff_id) => {
    if (!staff_id || !userCtx?.session?.user?.organization_id) {
      console.warn("Staff ID or Organization ID are incomplete. Page Employment job")
      return null
    }

    const res = await refetch({
      staff_id: staff_id,
      organization_id: userCtx.session.user.organization_id,
    })
    const { staff_name_first, staff_name_last } = res.data.user
    return { first: staff_name_first, last: staff_name_last }
  }

  useEffect(() => {
    setLoading(true)
    const getParticipantData = () => {
      const fetchJobSearchPlan = () => {
        return axios
          .get(
            `${api_url}/jobSearchPlan/${referralId}/${organizationId}/${participantId}`,
            { headers: { Authorization: `Bearer ${userCtx.tokenEmployment}` } }
          )
          .then(async (response) => {
            let jobSearchPlanData = null
            if (response && response.data) {
              jobSearchPlanData = response.data
              jobSearchPlanData.createdBy = await getStaffName(
                jobSearchPlanData.createdBy
              )
              if (jobSearchPlanData.updatedBy != null)
                jobSearchPlanData.updatedBy = await getStaffName(
                  jobSearchPlanData.updatedBy
                )
              setSearchPlanAction(Actions.Edit)

              if (jobSearchPlanData.searchGoals.length > 0) {
                //Find min target date
                let searchGoalsMinTargetDate = convertToLocalDateTime(
                  jobSearchPlanData.searchGoals[0].targetDate
                )
                jobSearchPlanData.searchGoals.forEach((goal) => {
                  const targetDate = convertToLocalDateTime(goal.targetDate)
                  if (targetDate < searchGoalsMinTargetDate) {
                    searchGoalsMinTargetDate = targetDate
                  }
                })
                setMinTargetDate(formatDate(searchGoalsMinTargetDate))
              }
              //Default target date is engagement date
              const engagementDate = formatDate(
                convertToLocalDateTime(jobSearchPlanData.engagementDate)
              )
              setSearchGoal({ ...searchGoal, targetDate: engagementDate })
            }
            setJobSearchPlan(jobSearchPlanData)
            return {
              success: true,
            }
          })
          .catch(function (error) {
            return { success: false, error: error }
          })
      }
      const fetchJobRecords = () => {
        return axios
          .get(
            `${api_url}/jobRecord/${referralId}/${organizationId}/${participantId}`,
            { headers: { Authorization: `Bearer ${userCtx.tokenEmployment}` } }
          )
          .then(async (response) => {
            let jobRecordsData = []
            if (response) {
              jobRecordsData = response.data
            }
            setJobRecords(jobRecordsData)
            return {
              success: true,
            }
          })
          .catch(function (error) {
            return { success: false, error: error }
          })
      }
      Promise.all([fetchJobSearchPlan(), fetchJobRecords()]).then(
        ([jobSearchPlanResponse, jobRecordsResponse]) => {
          if (!jobSearchPlanResponse.success) {
            const errors = jobSearchPlanResponse.error
            showError(toast, "Not able to get job search plan", errors)
            setJobSearchPlan(null)
          }
          if (!jobRecordsResponse.success) {
            const errors = jobRecordsResponse.error
            showError(toast, "Not able to get job records", errors)
            setJobRecords([])
          }
          setLoading(false)
        }
      )
    }
    getParticipantData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantId])

  const sortJobs = (jobs) => {
    jobs.sort((a, b) => {
      const s = a.ended - b.ended
      if (s === 0) {
        return new Date(b.createdAt) - new Date(a.createdAt)
      }
      return s
    })
    setJobRecords(jobs)
  }

  const renderJobsActions = (item) => {
    return (
      <div className={styles.table_actions}>
        <Button
          icon="pi pi-eye"
          className={styles.rounded_btn}
          onClick={() => {
            setJobRecordAction(Actions.View)
            setJobRecord(item)
            setShowJobRecordForm(true)
          }}
          disabled={props.inactive}
        />
        <Button
          className={styles.edit_btn}
          onClick={() => {
            setJobRecordAction(Actions.Edit)
            setJobRecord({ ...item })
            setShowJobRecordForm(true)
          }}
          disabled={props.inactive || props.viewMode?.view}
        />
        <Button
          className={styles.end_job_btn}
          onClick={() => {
            setJobRecord(item)
            setShowEndJobForm(true)
          }}
          disabled={props.inactive || props.viewMode?.view}
        />
      </div>
    )
  }

  const renderSearchGoalsActions = (item) => {
    return (
      <div className={styles.table_actions}>
        <Button
          className={styles.edit_btn}
          onClick={() => {
            const status = SearchGoalStatuses[item.status]
            const frequency = Frequencies[item.frequency]
            const targetDate = formatDate(
              convertToLocalDateTime(item.targetDate)
            )
            setSearchGoalAction(Actions.Edit)
            setSearchGoal({ ...item, status, frequency, targetDate })
            setSearchGoalDialogVisible(true)
          }}
          disabled={props.inactive || props.viewMode?.view}
        />
        <Button
          icon="pi pi-trash"
          className={styles.rounded_btn}
          onClick={() => {
            setSearchGoalToDelete(item)
            setDeleteConfirmationVisible(true)
          }}
          disabled={props.inactive || props.viewMode?.view}
        />
      </div>
    )
  }

  const renderJobSearchPlan = () => {
    const jobSearchPlanDic = [
      {
        label: "Engagement date",
        value: jobSearchPlan.engagementDate,
        type: "date",
        group: 1,
      },
      {
        label: "Engagement channel",
        value: EngagementChannels[jobSearchPlan.engagementChannel],
        type: "string",
        group: 1,
      },
      {
        label: "Engagement duration",
        value: getDuration(jobSearchPlan.startTime, jobSearchPlan.endTime),
        type: "string",
        group: 1,
      },
      {
        label: "Start/End time",
        value: getStartAndEndTime(
          jobSearchPlan.startTime,
          jobSearchPlan.endTime
        ),
        type: "string",
        group: 1,
      },
      {
        label: "Date opened",
        value: jobSearchPlan.createdAt,
        type: "datetime",
        group: 2,
      },
      {
        label: "Performed by",
        value: jobSearchPlan.createdBy,
        type: "staff",
        group: 2,
      },
      {
        label: "Last updated at",
        value: jobSearchPlan.updatedAt,
        type: "datetime",
        group: 2,
      },
      {
        label: "Last updated by",
        value: jobSearchPlan.updatedBy,
        type: "staff",
        group: 2,
      },
      {
        label: "What are the participant's job search goals?",
        value: jobSearchPlan.jobSearchGoals,
        type: "html",
        group: 3,
      },
      {
        label: "What are the participant's job related strengths?",
        value: jobSearchPlan.jobRelatedStrengths,
        type: "html",
        group: 4,
      },
    ]

    const basicInfo = groupBy(jobSearchPlanDic, "group")

    return (
      <>
        <div className={styles.rounded_wrapper}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              marginTop: "10px",
              marginBottom: "10px",
            }}
          >
            <ViewHeader
              viewMode={props.viewMode}
              title={"Job search plan"}
              closeViewMode={props.closeViewMode}
            />
            {jobSearchPlan != null && (
              <Button
                className={styles.edit_btn}
                onClick={() => setShowJobSearchPlanForm(true)}
                disabled={props.inactive || props.viewMode?.view}
              />
            )}
          </div>

          <div className={styles.table_group}>
            {Object.values(basicInfo).map((group, groupIndex) => {
              return (
                <table className={styles.table}>
                  <thead>
                    <tr>
                      {group.map((item, index) => (
                        <th key={index}>{item.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr key={`job_search_plan${groupIndex}`}>
                      {group.map((item, index) =>
                        renderTableDataCell(item, index)
                      )}
                    </tr>
                  </tbody>
                </table>
              )
            })}
          </div>
          <div className={styles.table_group}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <h3
                className={styles.view_subtitle}
                style={{
                  color: "#383838",
                  fontWeight: "600",
                  alignSelf: "center",
                }}
              >
                Job search goals
              </h3>
              <Button
                className={styles.inverted_button}
                type="submit"
                onClick={() => setSearchGoalDialogVisible(true)}
                disabled={props.inactive || props.viewMode?.view}
              >
                Add job search goal
              </Button>
            </div>

            {renderDeleteSearchGoalConfirmation()}

            <Dialog
              header="Job search goal"
              className={`custom_dialog ${styles.job_search_goal}`}
              visible={searchGoalDialogVisible}
              onHide={() => {
                setSearchGoalDialogVisible(false)
                clearSearchGoal()
              }}
            >
              <div
                style={{
                  padding: "10px",
                }}
              >
                <div className={styles.field}>
                  <label className={styles.label}>Goal</label>
                  <InputText
                    style={{ width: "100%" }}
                    value={searchGoal.goal}
                    onChange={(e) =>
                      setSearchGoal({ ...searchGoal, goal: e.target.value })
                    }
                    className="p-inputtext-md"
                    name="searchGoal_goal"
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>
                    Who is responsable for this goal?
                  </label>
                  <InputText
                    className="p-inputtext-md"
                    style={{ width: "100%" }}
                    value={searchGoal.responsable}
                    onChange={(e) =>
                      setSearchGoal({
                        ...searchGoal,
                        responsable: e.target.value,
                      })
                    }
                    name="searchGoal_responsable"
                  />
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Frequency</label>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <Dropdown
                      value={searchGoal.frequency}
                      style={{
                        marginTop: "0",
                        height: "32px",
                        alignItems: "center",
                      }}
                      options={Frequencies}
                      panelClassName="custom_dropdown_panel"
                      onChange={(e) =>
                        setSearchGoal({ ...searchGoal, frequency: e.value })
                      }
                      placeholder="Select a frequency"
                    />
                    {searchGoal.frequency === Frequencies[3] && ( //Other
                      <InputText
                        className="p-inputtext-md"
                        style={{ width: "100%" }}
                        placeholder={"Other: please specify here"}
                        value={searchGoal.otherFrequency}
                        onChange={(e) =>
                          setSearchGoal({
                            ...searchGoal,
                            otherFrequency: e.target.value,
                          })
                        }
                        name="searchGoal_otherFrequency"
                      />
                    )}
                  </div>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Status</label>
                  <div
                    className={styles.radio_btn_wrapper}
                    style={{ gap: "10px", flexWrap: "wrap" }}
                  >
                    {SearchGoalStatuses.map((status, index) => {
                      return (
                        <div className={styles.radio_btn}>
                          <RadioButton
                            key={`status${index}`}
                            value={status}
                            style={{ margin: "0" }}
                            onChange={(e) =>
                              setSearchGoal({ ...searchGoal, status: e.value })
                            }
                            checked={searchGoal.status === status}
                          />
                          <label
                            htmlFor={`status${index}`}
                            className={styles.inline_label}
                          >
                            {status}
                          </label>
                        </div>
                      )
                    })}
                  </div>
                </div>
                <div className={styles.field}>
                  <label className={styles.label}>Target Date</label>
                  <Calendar
                    name="Target date"
                    value={searchGoal.targetDate}
                    panelClassName="custom_calendar_panel"
                    onChange={(e) => {
                      if (e.value < minTargetDate) {
                        setMinTargetDate(formatDate(e.value))
                      }
                      setSearchGoal({ ...searchGoal, targetDate: e.value })
                    }}
                    dateFormat="mm/dd/yy"
                    showIcon
                    className={styles.calendar}
                    minDate={convertToLocalDateTime(
                      jobSearchPlan.engagementDate
                    )}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "20px",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    className={styles.cancel_button}
                    type="submit"
                    disabled={loadingSearchGoal}
                    onClick={() => {
                      setSearchGoalDialogVisible(false)
                      clearSearchGoal()
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className={styles.button}
                    type="submit"
                    style={{
                      width: "91px!important",
                      justifyContent: "center",
                    }}
                    disabled={loadingSearchGoal}
                    onClick={() => saveSearchGoal()}
                  >
                    {`${searchGoalAction === Actions.Add ? "Add" : "Update"}`}
                  </Button>
                </div>
              </div>
            </Dialog>

            {jobSearchPlan.searchGoals.length > 0 ? (
              jobSearchPlan.searchGoals.map((item) => {
                return (
                  <table className={styles.table} id="search_goals">
                    <thead>
                      <tr>
                        <th style={{ width: "30%" }} key="goal_th">
                          Goal
                        </th>
                        <th style={{ width: "12%" }} key="frequency_th">
                          Frequency
                        </th>
                        <th style={{ width: "14%" }} key="status_th">
                          Status
                        </th>
                        <th style={{ width: "14%" }} key="target_date_th">
                          Target date
                        </th>
                        <th style={{ width: "30%" }} key="responsable_th">
                          Owner
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {renderTableDataCell({
                          label: "goal",
                          value: item.goal,
                          type: "string",
                        })}
                        {renderTableDataCell({
                          label: "frequency",
                          value: Frequencies[item.frequency],
                          type: "string",
                        })}
                        <td key="status">
                          <span
                            className={`${styles.status_chip} ${item.status === 0
                                ? styles.status_active
                                : item.status === 1
                                  ? styles.status_complete
                                  : styles.status_inactive
                              }`}
                          >
                            {SearchGoalStatuses[item.status] != null
                              ? SearchGoalStatuses[item.status]
                              : ""}
                          </span>
                        </td>
                        {renderTableDataCell({
                          label: "target_date",
                          value: item.targetDate,
                          type: "date",
                        })}
                        {renderTableDataCell({
                          label: "responsable",
                          value: item.responsable,
                          type: "string",
                        })}
                        <td key="actions">{renderSearchGoalsActions(item)}</td>
                      </tr>
                    </tbody>
                  </table>
                )
              })
            ) : (
              <p>No search goals have been added</p>
            )}
          </div>
        </div>

        <div className={styles.rounded_wrapper} style={{ marginTop: "10px" }}>
          <div className={styles.table_group} style={{ marginTop: "0" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <h3
                className={styles.view_subtitle}
                style={{
                  color: "#383838",
                  fontWeight: "600",
                  alignSelf: "center",
                }}
              >
                Jobs
              </h3>
              <Button
                label="Add a job"
                className={styles.inverted_button}
                type="submit"
                onClick={() => {
                  setJobRecordAction(Actions.Add)
                  setJobRecord(null)
                  setShowJobRecordForm(true)
                }}
                disabled={props.inactive || props.viewMode?.view}
              />
            </div>

            {jobRecords.length > 0 ? (
              jobRecords.map((item, index) => {
                return (
                  <table
                    className={`${styles.table} ${styles.jobs_table}`}
                    id={`job${index}`}
                  >
                    <thead>
                      <tr>
                        <th key="job_title_th" style={{ width: "22%" }}>
                          Job title
                        </th>
                        <th key="employer_name_th" style={{ width: "20%" }}>
                          Employer name
                        </th>
                        <th key="created_at_th" style={{ width: "12%" }}>
                          Date added
                        </th>
                        <th
                          key="first_employment_day_th"
                          style={{ width: "12%" }}
                        >
                          First day
                        </th>
                        <th key="supervisor_name_th" style={{ width: "20%" }}>
                          Supervisor
                        </th>
                        <th key="status_th" style={{ width: "10%" }}>
                          Status
                        </th>
                        <th key="actions_th" />
                      </tr>
                    </thead>
                    <tbody>
                      <tr key={index}>
                        {renderTableDataCell({
                          label: "jobTitle",
                          value: item.jobTitle,
                          type: "string",
                        })}
                        {renderTableDataCell({
                          label: "employerName",
                          value: item.employerName,
                          type: "string",
                        })}
                        {renderTableDataCell({
                          label: "createdAt",
                          value: item.createdAt,
                          type: "date",
                        })}
                        {renderTableDataCell({
                          label: "firstEmploymentDay",
                          value: item.firstEmploymentDay,
                          type: "date",
                        })}
                        {renderTableDataCell({
                          label: "supervisorName",
                          value: item.supervisorName,
                          type: "string",
                        })}
                        <td key="status">
                          <span
                            className={`${styles.status_chip} ${item.ended
                                ? styles.status_inactive
                                : styles.status_active
                              }`}
                          >
                            {item.ended ? "Ended" : "Active"}
                          </span>
                        </td>
                        <td>{renderJobsActions(item)}</td>
                      </tr>
                    </tbody>
                  </table>
                )
              })
            ) : (
              <p>No jobs have been added</p>
            )}
          </div>
        </div>
      </>
    )
  }

  const renderNoJobSearchPlan = () => {
    return (
      <div className={styles.no_record_div}>
        <NoData
          icon={
            <TaskCompleteIcon
              style={{ padding: "8px", width: "100%", height: "100%" }}
            />
          }
        />
        <div style={{ width: "600px" }}>
          <p style={{ fontWeight: "700" }}>
            A job search plan must be completed before adding jobs
          </p>
        </div>
        <Button
          className={styles.button}
          label="Create job search plan"
          onClick={() => setShowJobSearchPlanForm(true)}
          disabled={props.inactive || props.viewMode?.view}
        />
      </div>
    )
  }

  const renderJobIndex = () => {
    return (
      <>
        {loading && (
          <div style={{ width: "100%" }}>
            <LoaderMedium />
          </div>
        )}
        {!loading && (
          <div className={styles.job_search_plan}>
            {jobSearchPlan == null && renderNoJobSearchPlan()}
            {jobSearchPlan != null && renderJobSearchPlan()}
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />
      {showJobSearchPlanForm && (
        <JobSearchPlan
          closeForm={() => setShowJobSearchPlanForm(false)}
          action={searchPlanAction}
          jobSearchPlan={jobSearchPlan}
          setSearchGoalDefaultTargetDate={(date) =>
            setSearchGoal({ ...searchGoal, targetDate: date })
          }
          minTargetDate={minTargetDate}
          updateData={async (data) => {
            setLoading(true)
            data.createdBy = await getStaffName(data.createdBy)
            if (data.updatedBy != null)
              data.updatedBy = await getStaffName(data.updatedBy)
            setSearchPlanAction(Actions.Edit)
            setJobSearchPlan({ ...jobSearchPlan, ...data })
            setLoading(false)
          }}
        />
      )}
      {showJobRecordForm && props.viewMode?.view && (
        <JobRecordView
          jobRecord={jobRecord}
          closeForm={() => setShowJobRecordForm(false)}
          loading={loading}
          viewMode={props.viewMode}
        />
      )}
      {showJobRecordForm && !props.viewMode?.view && (
        <JobRecord
          closeForm={() => setShowJobRecordForm(false)}
          action={jobRecordAction}
          jobRecord={jobRecord}
          jobSearchPlanId={jobSearchPlan.id}
          updateData={(data) => {
            if (jobRecordAction === Actions.Add) {
              sortJobs([...jobRecords, data])
            } else {
              const updatedRecords = jobRecords.map((r) => {
                if (r.id === data.id) {
                  return data
                }
                return r
              })
              setJobRecords(updatedRecords)
            }
          }}
        />
      )}

      {!showJobSearchPlanForm && !showJobRecordForm && showEndJobForm && (
        <EndJobForm
          closeForm={() => setShowEndJobForm(false)}
          jobRecord={jobRecord}
          action={Actions.End}
          updateData={(data) => {
            const updatedRecords = jobRecords.map((r) => {
              if (r.id === data.id) {
                return data
              }
              return r
            })
            setJobRecords(updatedRecords)
          }}
        />
      )}

      {!showJobSearchPlanForm &&
        !showJobRecordForm &&
        !showEndJobForm &&
        renderJobIndex()}
    </>
  )
}
